import { useEffect, useState } from 'react';

import { FormHelperText, Grid, MenuItem } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';

import AddRowButton from '../../../components/inputRow/AddRowButton';
import RemoveRowButton from '../../../components/inputRow/RemoveRowButton';
import { UserRoles } from '../../../components/roster/types';
import { useOrgSettings } from '../../../hooks/useOrgSettings';
import {
    StyledErrorIcon,
    StyledInputLabel,
    StyledTextField,
    StyledSelect,
    StyledGridRowContainer
} from '../../../styles/input-row';
import { blankStudent } from '../constants/inviteStudentsDefaults';
import {
    type EmailErrorKeys,
    emailValidation,
    type ExtUserIdErrorKeys,
    extUserIdValidation,
    firstNameValidation,
    lastNameValidation,
    type NameErrorKeys,
    type RoleErrorKeys,
    roleValidation
} from '../utils/inviteStudentsValidation';

interface InviteStudentsRowProps {
    addRow: () => void;
    handleChange: (
        index: number,
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => void;
    removeRow: (index: number) => void;
    student: { firstName: string; lastName: string; email?: string; extUserId?:string; role?: UserRoles };
    index: number;
    lastRow: boolean;
    canSubmit: boolean;
    showErrors?: boolean;
    isDuplicateEmail: boolean;
    isDuplicateExtUserId: boolean;
}

const useStyles = makeStyles({
    error: {
        marginTop: '0px',
        display: 'flex'
    }
});

const InviteStudentsRow = (props: InviteStudentsRowProps) => {
    const { addRow, removeRow, handleChange, student, index, lastRow, canSubmit, showErrors, isDuplicateEmail, isDuplicateExtUserId } = props;

    const { t } = useTranslation('invite_students');
    const { t: tRoster } = useTranslation('roster');
    const classes = useStyles();

    const { data: orgSettings } = useOrgSettings();

    const [firstNameError, setFirstNameError] = useState<NameErrorKeys>('');
    const [lastNameError, setLastNameError] = useState<NameErrorKeys>('');
    const [emailError, setEmailError] = useState<EmailErrorKeys>('');
    const [extUserIdError, setExtUserIdError] = useState<ExtUserIdErrorKeys>('');
    const [roleError, setRoleError] = useState<RoleErrorKeys>('');

    const renderRowButton = () => {
        if (lastRow) {
            return <AddRowButton addRow={addRow} />;
        }
        return <RemoveRowButton index={index} removeRow={removeRow} />;
    };

    const renderErrorMessage = (errorKey: string) => {
        let adjustedErrorKey = errorKey;
        if (!orgSettings?.featureSettings.options.useIdentifierInRoster && errorKey === 'required-email-or-ext-user-id') {
            adjustedErrorKey = 'required-email';
        }
        const errorMessage = t(`errors.${adjustedErrorKey}`);
        return (
            <>
                <StyledErrorIcon />
                {errorMessage}
            </>
        );
    };

    const removeErrors = () => {
        setFirstNameError('');
        setLastNameError('');
        setEmailError('');
        setExtUserIdError('');
    };

    const blurValidation = (field: 'firstName' | 'lastName' | 'email' | 'extUserId') => {
        switch (field) {
            case 'firstName':
                if (isEqual(student, blankStudent)) {
                    removeErrors();
                } else {
                    setFirstNameError(firstNameValidation(student.firstName));
                }
                break;

            case 'lastName':
                if (isEqual(student, blankStudent)) {
                    removeErrors();
                } else {
                    setLastNameError(lastNameValidation(student.lastName));
                }
                break;

            case 'email':
                if (isEqual(student, blankStudent)) {
                    removeErrors();
                } else if (isDuplicateEmail) {
                    setEmailError('duplicate-email');
                } else {
                    setEmailError(emailValidation(student.email, student.extUserId));
                    setExtUserIdError(extUserIdValidation(student.extUserId, student.email));
                }
                break;
            
            case 'extUserId':
                if (isEqual(student, blankStudent)) {
                    removeErrors();
                } else if (isDuplicateExtUserId) {
                    setExtUserIdError('duplicate-ext-user-id');
                }
                 else {
                    setEmailError(emailValidation(student.email, student.extUserId));
                    setExtUserIdError(extUserIdValidation(student.extUserId, student.email));
                }
                break

            default:
                break;
        }
    };

    useEffect(() => {
        setRoleError(roleValidation(student.role));
    }, [student.role]);

    useEffect(() => {
        removeErrors();
    }, [canSubmit]);

    useEffect(() => {
        if (showErrors) {
            setFirstNameError(firstNameValidation(student.firstName));
            setLastNameError(lastNameValidation(student.lastName));
            if (isDuplicateEmail) {
                setEmailError('duplicate-email');
            } else {
                setEmailError(emailValidation(student.email, student.extUserId));
            }
            if (isDuplicateExtUserId) {
                setExtUserIdError('duplicate-ext-user-id');
            }
            else {
                setExtUserIdError(extUserIdValidation(student.extUserId, student.email));
            }

        }
    }, [student, showErrors, isDuplicateEmail, isDuplicateExtUserId]);

    return (
        <StyledGridRowContainer container spacing={2}>
            <Grid item xs={12} sm={6} md={2}>
                <StyledInputLabel htmlFor={`firstName-${index}`}>{t('simple-mode.first-name')}</StyledInputLabel>
                <StyledTextField
                    id={`firstName-${index}`}
                    name="firstName"
                    value={student.firstName}
                    variant="outlined"
                    size="small"
                    onChange={(event) => handleChange(index, event)}
                    onBlur={() => blurValidation('firstName')}
                    helperText={
                        firstNameError === '' ? (
                            ''
                        ) : (
                            <span id={`firstNameError-${index}`}>{renderErrorMessage(firstNameError)}</span>
                        )
                    }
                    error={firstNameError !== ''}
                    aria-describedby={firstNameError ? `firstNameError-${index}` : undefined}
                    FormHelperTextProps={{ classes: { error: classes.error } }}
                />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
                <StyledInputLabel htmlFor={`lastName-${index}`}>{t('simple-mode.last-name')}</StyledInputLabel>
                <StyledTextField
                    id={`lastName-${index}`}
                    name="lastName"
                    value={student.lastName}
                    variant="outlined"
                    size="small"
                    onChange={(event) => handleChange(index, event)}
                    onBlur={() => blurValidation('lastName')}
                    helperText={
                        lastNameError === '' ? (
                            ''
                        ) : (
                            <span id={`lastNameError-${index}`}>{renderErrorMessage(lastNameError)}</span>
                        )
                    }
                    error={lastNameError !== ''}
                    aria-describedby={lastNameError ? `lastNameError-${index}` : undefined}
                    FormHelperTextProps={{ classes: { error: classes.error } }}
                />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
                <StyledInputLabel htmlFor={`email-${index}`}>{t('simple-mode.email')}</StyledInputLabel>
                <StyledTextField
                    id={`email-${index}`}
                    name="email"
                    value={student.email}
                    variant="outlined"
                    size="small"
                    onChange={(event) => handleChange(index, event)}
                    onBlur={() => blurValidation('email')}
                    helperText={
                        emailError === '' ? (
                            ''
                        ) : (
                            <span id={`emailError-${index}`}>{renderErrorMessage(emailError)}</span>
                        )
                    }
                    error={emailError !== ''}
                    aria-describedby={emailError ? `emailError-${index}` : undefined}
                    FormHelperTextProps={{ classes: { error: classes.error } }}
                />
            </Grid>

            {orgSettings?.featureSettings.options.useIdentifierInRoster && <Grid item xs={12} sm={6} md={2}>
                <StyledInputLabel htmlFor={`ext-user-id-${index}`}>{t('simple-mode.ext-user-id')}</StyledInputLabel>
                <StyledTextField
                    id={`ext-user-id-${index}`}
                    name="extUserId"
                    value={student.extUserId}
                    variant="outlined"
                    size="small"
                    onChange={(event) => handleChange(index, event)}
                    onBlur={() => blurValidation('extUserId')}
                    helperText={
                        extUserIdError === '' ? (
                            ''
                        ) : (
                            <span id={`extUserIdError-${index}`}>{renderErrorMessage(extUserIdError)}</span>
                        )
                    }
                    error={extUserIdError !== ''}
                    aria-describedby={extUserIdError ? `extUserIdError-${index}` : undefined}
                    FormHelperTextProps={{ classes: { error: classes.error } }}
                />
            </Grid>}

            <Grid item xs={12} sm={4} md={2}>
                <StyledInputLabel id={`user-role-label-${index}`}>{t('simple-mode.role')}</StyledInputLabel>
                <StyledSelect
                    name="role"
                    value={student.role}
                    labelId={`user-role-label-${index}`}
                    onChange={(event) => handleChange(index, event as any)}
                    error={!!roleError}
                    aria-describedby={roleError ? `roleError-${index}` : undefined}
                >
                    <MenuItem value={UserRoles.Student}>{tRoster('roles.student')}</MenuItem>
                    {orgSettings?.featureSettings.options.allowNextGenGuests && <MenuItem value={UserRoles.Guest}>{tRoster('roles.guest')}</MenuItem>}
                    <MenuItem value={UserRoles.Assistant}>{tRoster('roles.assistant')}</MenuItem>
                    <MenuItem value={UserRoles.Teacher}>{tRoster('roles.teacher')}</MenuItem>
                </StyledSelect>

                {!!roleError && (
                    <FormHelperText id={`roleError-${index}`} aria-live="polite">
                        {renderErrorMessage(roleError)}
                    </FormHelperText>
                )}
            </Grid>

            <Grid item xs={12} sm={2} md={1}>
                {renderRowButton()}
            </Grid>
        </StyledGridRowContainer>
    );
};

export default InviteStudentsRow;
